const REDACTED_TOKEN = "[REDACTED_TOKEN]";
const REDACTED_PATH = "[REDACTED_PATH]";

/**
 * Sanitize an error message for safe user-facing display.
 *
 * Removes or redacts:
 *  - HTML bodies (e.g. Cloudflare 5xx pages leaking through as raw HTML)
 *  - GitLab and GitHub token prefixes
 *  - Bearer tokens
 *  - OAuth token parameters/properties
 *  - Stack trace lines
 *  - Absolute file paths
 *  - Project/repo path segments (group/project-name patterns)
 *
 * Works in both Node.js and browser environments — pure regex, no dependencies.
 */
export function sanitizeErrorMessage(message: string): string {
  // Strip HTML bodies (e.g. Cloudflare 5xx pages) before other sanitization
  if (/<[a-z/][\s\S]*>/i.test(message)) {
    const statusMatch = message.match(/(\d{3})/);
    const status = statusMatch ? statusMatch[1] : "unknown";
    return `GitLab returned an error (HTTP ${status}). Try again in a moment.`;
  }

  let sanitized = message;

  // Redact GitLab PAT tokens
  sanitized = sanitized.replace(/\bglpat-[A-Za-z0-9_-]+\b/g, REDACTED_TOKEN);
  // Redact GitHub personal, OAuth, app user, and installation tokens
  sanitized = sanitized.replace(/\bgithub_pat_[A-Za-z0-9_]+\b/g, REDACTED_TOKEN);
  sanitized = sanitized.replace(/\bgh[opsru]_[A-Za-z0-9_]+\b/g, REDACTED_TOKEN);
  // Redact Bearer tokens
  sanitized = sanitized.replace(/\bBearer\s+[^\s,;]+/gi, `Bearer ${REDACTED_TOKEN}`);
  // Redact OAuth tokens embedded in URLs, form bodies, or JSON-ish error payloads
  sanitized = sanitized.replace(
    /([?&](?:access_token|refresh_token|id_token|token)=)[^&#\s]+/gi,
    `$1${REDACTED_TOKEN}`
  );
  sanitized = sanitized.replace(
    /((?:"(?:access_token|refresh_token|id_token|token)"|'(?:access_token|refresh_token|id_token|token)'|\b(?:access_token|refresh_token|id_token|token)\b)\s*[:=]\s*["']?)[^"',\s}&]+/gi,
    `$1${REDACTED_TOKEN}`
  );
  // Strip stack trace lines
  sanitized = sanitized.replace(/(?:^|\n)\s*at .*(?=\n|$)/g, "");
  // Scrub absolute file paths
  sanitized = sanitized.replace(
    /\/(?:Users|src|var|home|tmp|opt)\/[^\s:]+(?::\d+(?::\d+)?)?/g,
    REDACTED_PATH
  );
  // Replace project/repo path segments (e.g. "group/project-name")
  sanitized = sanitized.replace(/\b[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+\b/g, "project");

  return sanitized.replace(/\n{2,}/g, "\n").trim();
}
